/**
 * PKCE in the browser, for the console's own sign-in.
 *
 * The dataplane's flow (`packages/identity/src/oauth.ts`) is the same protocol run **server-side**,
 * where the verifier can live in an `httpOnly` cookie. Here there is no server: the console is a
 * static bundle, so the verifier has to survive a full-page redirect inside the browser.
 *
 * That is the one place this app touches web storage, and §0.2 rule 4 has to be read carefully
 * rather than waved at. The rule bans storing **tokens, sessions, and credentials** there, because
 * fragments share a tenant's origin and can read anything in it. Two things make this different:
 *
 * 1. `console.{baseDomain}` is its own origin and hosts no fragments. Nothing else runs here.
 * 2. A PKCE verifier is not a credential. It is a one-time nonce that is worthless without the
 *    authorization code it pairs with, it never grants access to anything by itself, and it is
 *    deleted the moment it is consumed.
 *
 * `sessionStorage`, not `localStorage`: it dies with the tab, which is the correct lifetime for
 * something that must outlive exactly one redirect. **The token that comes back is never stored** —
 * it stays in React state, as the pasted token does today.
 */

const VERIFIER_KEY = 'braid.pkce.verifier';
const STATE_KEY = 'braid.pkce.state';
const RETURN_KEY = 'braid.pkce.returnTo';

export interface PkceChallenge {
  verifier: string;
  challenge: string;
  state: string;
}

function base64url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(byteLength: number): string {
  return base64url(crypto.getRandomValues(new Uint8Array(byteLength)));
}

/** Mints a verifier, its S256 challenge, and an independent CSRF state. */
export async function createPkceChallenge(): Promise<PkceChallenge> {
  const verifier = randomString(64);
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));

  return {
    verifier,
    challenge: base64url(new Uint8Array(digest)),
    // Independent of the verifier: they defend against different attackers, and sharing one
    // would let a fix to either silently weaken the other.
    state: randomString(32),
  };
}

/**
 * Reduces a return target to a same-origin path.
 *
 * The same defence as the dataplane's `safeReturnPath`, for the same reason: this value survives
 * a redirect and is then navigated to. Deliberately duplicated rather than shared — the identity
 * package is server-side and pulling it into the browser bundle would drag `jose` and the AWS SDK
 * behind it. `pkce.spec.ts` runs the dataplane's own vectors against this one so they cannot drift.
 */
export function safeReturnPath(candidate: string | null | undefined): string {
  if (!candidate) return '/';

  // Explicit escapes, not the literal bytes: a browser strips tabs and newlines from a URL
  // before following it, and invisible characters in a security check are a bad idea.
  // eslint-disable-next-line no-control-regex
  const cleaned = candidate.replace(/[\u0000-\u0020\u007f]/g, '');
  if (!cleaned.startsWith('/')) return '/';
  if (/^[/\\]{2}/.test(cleaned)) return '/';

  try {
    const url = new URL(cleaned, 'https://placeholder.invalid');
    if (url.origin !== 'https://placeholder.invalid') return '/';
    return `${url.pathname}${url.search}${url.hash}` || '/';
  } catch {
    return '/';
  }
}

export interface PendingFlow {
  verifier: string;
  state: string;
  returnTo: string;
}

/** Remembers the in-flight attempt across the redirect. */
export function rememberFlow(flow: PkceChallenge, returnTo: string, storage: Storage = sessionStorage): void {
  storage.setItem(VERIFIER_KEY, flow.verifier);
  storage.setItem(STATE_KEY, flow.state);
  storage.setItem(RETURN_KEY, safeReturnPath(returnTo));
}

/**
 * Reads the attempt back and **clears it**, whatever happens next.
 *
 * Clearing on read rather than on success is deliberate: a verifier is single-use, and one left
 * behind after a failed callback is one an attacker can try to pair with a code of their own.
 */
export function consumeFlow(storage: Storage = sessionStorage): PendingFlow | null {
  const verifier = storage.getItem(VERIFIER_KEY);
  const state = storage.getItem(STATE_KEY);
  const returnTo = storage.getItem(RETURN_KEY);

  storage.removeItem(VERIFIER_KEY);
  storage.removeItem(STATE_KEY);
  storage.removeItem(RETURN_KEY);

  if (!verifier || !state) return null;
  // Re-checked coming out of storage, which has been outside this module's control.
  return { verifier, state, returnTo: safeReturnPath(returnTo) };
}

export interface ConsoleAuthConfig {
  region: string;
  userPoolId: string;
  clientId: string;
  domain: string;
}

export function authorizeUrl(config: ConsoleAuthConfig, flow: PkceChallenge, redirectUri: string): string {
  const url = new URL(`https://${config.domain}/oauth2/authorize`);

  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', config.clientId);
  url.searchParams.set('redirect_uri', redirectUri);
  url.searchParams.set('state', flow.state);
  url.searchParams.set('code_challenge', flow.challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('scope', 'openid email profile');

  return url.toString();
}

/** Where Cognito sends the browser back. Must match a callback URL on the app client exactly. */
export function redirectUriFor(origin: string): string {
  return `${origin}/callback`;
}
